import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Plus, RotateCcw, Save, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useT, type StringKey } from '@/i18n';
import { toast } from '@/stores/toast.store';
import { cn } from '@/lib/utils';
import { fmtNy } from '@/pages/navigator/labels';
import { kbApi, KB_KEYS, type KbArticle, type KbCategoria } from '@/features/navigator/kb.api';

/**
 * The knowledge base editor.
 *
 * What admin writes here reaches the navigators on their next search -- no
 * deploy. That is the whole reason these answers live in a table instead of a
 * constant: the FAQ changes when the questions change, and the questions change
 * every week.
 *
 * Deactivating rather than deleting, on purpose: an article that was quoted to
 * members for a month is a record of what they were told.
 */

const CATS: KbCategoria[] = ['faq', 'momentos', 'operativo', 'referencia'];

// F5-R4a — los grupos del dibujo son las categorías VIVAS de la tabla (los
// nombres del mock eran demo). Etiqueta y nota por categoría, por i18n.
const CAT_LABEL: Record<KbCategoria, StringKey> = {
  faq: 'f5r4a.kb.cat.faq',
  momentos: 'f5r4a.kb.cat.momentos',
  operativo: 'f5r4a.kb.cat.operativo',
  referencia: 'f5r4a.kb.cat.referencia',
};
const CAT_NOTE: Record<KbCategoria, StringKey> = {
  faq: 'f5r4a.kb.note.faq',
  momentos: 'f5r4a.kb.note.momentos',
  operativo: 'f5r4a.kb.note.operativo',
  referencia: 'f5r4a.kb.note.referencia',
};

type Draft = {
  slug: string;
  titulo: string;
  categoria: KbCategoria;
  bodyEs: string;
  bodyEn: string;
  keywords: string;
  fuente: string;
};

const EMPTY: Draft = {
  slug: '', titulo: '', categoria: 'faq', bodyEs: '', bodyEn: '', keywords: '', fuente: '',
};

function errText(err: unknown): string {
  const payload = (err as { payload?: { message?: unknown } })?.payload;
  const inner = payload?.message;
  if (inner && typeof inner === 'object' && !Array.isArray(inner)) {
    return (inner as { message?: string }).message ?? String((err as Error).message);
  }
  return (err as Error)?.message ?? 'Error';
}

export function KbEditor(): JSX.Element {
  const t = useT();
  const qc = useQueryClient();
  const [editing, setEditing] = useState<KbArticle | null>(null);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState<Draft>(EMPTY);
  // F5-R4a: la tarjeta de un grupo filtra la lista (toggle, client-side).
  const [catFilter, setCatFilter] = useState<KbCategoria | null>(null);

  const listQ = useQuery({ queryKey: KB_KEYS.admin, queryFn: () => kbApi.listAll() });

  const invalidate = (): void => {
    void qc.invalidateQueries({ queryKey: KB_KEYS.admin });
    // The navigator's searches are a different key; drop them all so a change
    // shows up on the next open instead of after the cache expires.
    void qc.invalidateQueries({ queryKey: ['navigator', 'kb'] });
  };

  const createMut = useMutation({
    mutationFn: () =>
      kbApi.create({
        slug: draft.slug,
        titulo: draft.titulo,
        categoria: draft.categoria,
        bodyEs: draft.bodyEs,
        bodyEn: draft.bodyEn || null,
        keywords: draft.keywords,
        fuente: draft.fuente || undefined,
      }),
    onSuccess: () => {
      invalidate();
      setCreating(false);
      setDraft(EMPTY);
      toast.success(t('adminNav.kb.created'), '');
    },
    onError: (e) => toast.error(t('adminNav.kb.failed'), errText(e)),
  });

  const updateMut = useMutation({
    mutationFn: (vars: { id: string; body: Partial<KbArticle> }) =>
      kbApi.update(vars.id, vars.body as never),
    onSuccess: () => {
      invalidate();
      setEditing(null);
      toast.success(t('adminNav.kb.saved'), '');
    },
    onError: (e) => toast.error(t('adminNav.kb.failed'), errText(e)),
  });

  const articles = listQ.data ?? [];
  const groups = CATS.map((c) => {
    const arts = articles.filter((a) => a.categoria === c);
    const latest = arts.reduce<string | null>(
      (max, a) => (!max || a.updatedAt > max ? a.updatedAt : max),
      null,
    );
    return { cat: c, n: arts.length, latest };
  });
  const groupCount = groups.filter((g) => g.n > 0).length;
  const shown = catFilter ? articles.filter((a) => a.categoria === catFilter) : articles;

  return (
    <div className="space-y-4 font-body text-nav-navy">
      {/* F5-R4a — el conteo vivo arriba y «Nuevo artículo» a la derecha. */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <p className="font-head text-[10.5px] font-bold tracking-[0.14em] uppercase text-nav-teal">
            {t('f5r4a.kb.kicker', { n: articles.length, g: groupCount })}
          </p>
          <p className="text-[13px] text-nav-slate mt-1">{t('adminNav.kb.intro')}</p>
        </div>
        <Button size="sm" onClick={() => { setCreating(true); setDraft(EMPTY); }}>
          <Plus className="h-4 w-4" aria-hidden />
          {t('adminNav.kb.new')}
        </Button>
      </div>

      {/* F5-R4a — las tarjetas de grupo (categorías vivas). Tocar una filtra
          la lista de abajo; tocarla de nuevo la suelta. */}
      <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))' }}>
        {groups.map((g) => {
          const on = catFilter === g.cat;
          return (
            <button
              key={g.cat}
              type="button"
              aria-pressed={on}
              aria-label={t('f5r4a.kb.groupAria', { cat: t(CAT_LABEL[g.cat]) })}
              onClick={() => setCatFilter(on ? null : g.cat)}
              className={cn(
                'rounded-xl border-[1.5px] bg-white px-5 py-[18px] text-left transition-colors duration-150 motion-reduce:transition-none',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal',
                on ? 'border-nav-teal bg-nav-tealTint/40' : 'border-nav-card hover:border-nav-teal',
              )}
            >
              <span className="flex items-baseline justify-between gap-3">
                <span className="font-head text-[13.5px] font-bold text-nav-navy">{t(CAT_LABEL[g.cat])}</span>
                <span className="font-head text-[17px] font-extrabold tabular-nums text-nav-navy">{g.n}</span>
              </span>
              <span className="block mt-2 text-[12.5px] leading-normal text-nav-slate">{t(CAT_NOTE[g.cat])}</span>
              {g.latest && (
                <span className="block mt-2.5 text-[12px] text-nav-faint tabular-nums">
                  {t('f5r4a.kb.edited', { date: fmtNy(g.latest, 'date') })}
                </span>
              )}
            </button>
          );
        })}
      </div>
      {catFilter && (
        <p className="text-[12px] text-nav-slate">
          {t('f5r4a.kb.filterOn', { cat: t(CAT_LABEL[catFilter]) })}{' '}
          <button type="button" className="underline" onClick={() => setCatFilter(null)}>
            {t('f5r4a.kb.filterOff')}
          </button>
        </p>
      )}

      {creating && (
        <Form
          draft={draft}
          setDraft={setDraft}
          onCancel={() => setCreating(false)}
          onSave={() => createMut.mutate()}
          pending={createMut.isPending}
          withSlug
        />
      )}

      {listQ.isLoading && (
        <p className="text-sm text-nav-slate flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> {t('common.loading')}
        </p>
      )}

      <div className="rounded-xl border-[1.5px] border-nav-card bg-white divide-y divide-nav-hair overflow-hidden">
        {shown.map((a) => (
          <div key={a.id} className={'px-3 py-2.5 ' + (a.active ? '' : 'bg-nav-softer')}>
            {editing?.id === a.id ? (
              <Form
                draft={{
                  slug: a.slug, titulo: editing.titulo, categoria: editing.categoria,
                  bodyEs: editing.bodyEs, bodyEn: editing.bodyEn ?? '',
                  keywords: editing.keywords, fuente: editing.fuente,
                }}
                setDraft={(d) => setEditing({ ...editing, ...d, bodyEn: d.bodyEn || null })}
                onCancel={() => setEditing(null)}
                onSave={() =>
                  updateMut.mutate({
                    id: a.id,
                    body: {
                      titulo: editing.titulo, categoria: editing.categoria, bodyEs: editing.bodyEs,
                      bodyEn: editing.bodyEn, keywords: editing.keywords, fuente: editing.fuente,
                    },
                  })
                }
                pending={updateMut.isPending}
              />
            ) : (
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p className="font-head text-[13.5px] font-bold text-nav-navy leading-snug">
                    {a.titulo}
                    {!a.active && (
                      <span className="ml-2 font-head text-[10px] font-bold uppercase tracking-wide text-nav-slate border border-nav-line rounded-full px-1.5 py-0.5">
                        {t('adminNav.kb.inactive')}
                      </span>
                    )}
                  </p>
                  <p className="text-[11px] text-nav-slate mt-0.5">
                    {a.categoria} · {a.slug} · {a.fuente}
                  </p>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <Button variant="ghost" size="sm" onClick={() => setEditing(a)}>
                    {t('common.edit')}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => updateMut.mutate({ id: a.id, body: { active: !a.active } })}
                    disabled={updateMut.isPending}
                  >
                    {a.active ? <X className="h-4 w-4" aria-hidden /> : <RotateCcw className="h-4 w-4" aria-hidden />}
                    {a.active ? t('adminNav.kb.deactivate') : t('adminNav.kb.reactivate')}
                  </Button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* F5-R4a — la nota del dibujo: publicado vs. desactivado, y quién edita. */}
      <p className="rounded-[10px] border-[1.5px] border-nav-card bg-white px-4 py-3.5 text-[12.5px] leading-relaxed text-nav-slate">
        {t('f5r4a.kb.note')}
      </p>
    </div>
  );
}

function Form({
  draft, setDraft, onCancel, onSave, pending, withSlug,
}: {
  draft: Draft;
  setDraft: (d: Draft) => void;
  onCancel: () => void;
  onSave: () => void;
  pending: boolean;
  withSlug?: boolean;
}): JSX.Element {
  const t = useT();
  const set = (k: keyof Draft, v: string): void => setDraft({ ...draft, [k]: v });
  const input = 'w-full rounded-lg border-[1.5px] border-nav-card bg-white px-2.5 py-1.5 text-sm text-nav-navy placeholder:text-nav-faint focus:outline-none focus:ring-2 focus:ring-nav-teal';
  return (
    <div className="space-y-2 py-1">
      {withSlug && (
        <input className={input} placeholder={t('adminNav.kb.slug')} value={draft.slug}
               onChange={(e) => set('slug', e.target.value)} />
      )}
      <input className={input} placeholder={t('adminNav.kb.titulo')} value={draft.titulo}
             onChange={(e) => set('titulo', e.target.value)} />
      <select className={input} value={draft.categoria}
              onChange={(e) => set('categoria', e.target.value)}>
        {CATS.map((c) => <option key={c} value={c}>{c}</option>)}
      </select>
      <textarea className={input} rows={4} placeholder={t('adminNav.kb.bodyEs')} value={draft.bodyEs}
                onChange={(e) => set('bodyEs', e.target.value)} />
      <textarea className={input} rows={3} placeholder={t('adminNav.kb.bodyEn')} value={draft.bodyEn}
                onChange={(e) => set('bodyEn', e.target.value)} />
      <input className={input} placeholder={t('adminNav.kb.keywords')} value={draft.keywords}
             onChange={(e) => set('keywords', e.target.value)} />
      <input className={input} placeholder={t('adminNav.kb.fuente')} value={draft.fuente}
             onChange={(e) => set('fuente', e.target.value)} />
      <p className="text-[11px] text-nav-slate leading-snug">{t('adminNav.kb.moneyNote')}</p>
      <div className="flex justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={onCancel}>{t('common.cancel')}</Button>
        <Button size="sm" onClick={onSave} disabled={pending}>
          {pending ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <Save className="h-4 w-4" aria-hidden />}
          {t('common.save')}
        </Button>
      </div>
    </div>
  );
}
